import { FC, useMemo } from 'react';
import {
  Button,
  ListTotal,
  TableToolbar,
  TableWrapper,
  Table,
  Typography,
  ActionsMenu,
  Footer,
} from 'melp-design/components';
import { Box, Divider, Stack } from '@mui/material';
import { AccessTime, ViewTimelineOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useRouteParams } from 'utils/useIdParam';
import { APP, ROUTES } from 'config';
import { useModalContext } from 'store/modal';
import {
  CustomCategoryValuesFilter,
  EmployeesFilter,
  EmployeesGroupsFilter,
  FilterButton,
  Filters,
  OrgStructureFilter,
  SelectFilter,
} from 'components/filters';
import { useSearchParams } from 'utils/navigation';
import { usePredefinedToasts } from 'utils/Toast';
import {
  COLOR_BY_STATUS,
  assignmentStatuses,
  employeesByBenefitFiltersSchema,
  useAssignEmployeesToBenefit,
  useEmployeesByBenefitAssignments,
  useExportEmployeesByBenefit,
} from 'store/assignments';
import {
  AssignmentDateSelectModal,
  EmployeeBenefitAssignmentHistory,
  EmployeeBenefitAssignmentTimeline,
} from 'containers/assignments';
import { DownloadXlsAction } from 'components/actions';
import { useMe } from 'state/Administrators';
import { CompanyScopeInfoMessage } from 'containers/employees';
import { EmployeeCell } from 'components/table';
import { CustomCategorySelectModal } from './atoms';

interface Props {
  onSaveSuccess: () => void;
  guided: boolean;
}

export const BenefitEmployeesAssignments: FC<Props> = ({
  onSaveSuccess,
  guided,
}) => {
  const { t } = useTranslation();
  const { id: benefitId } = useRouteParams(ROUTES.benefits.details);
  const { canEditBenefits, hasEditPermission, hasViewPermission } = useMe();
  const { openModal } = useModalContext();
  const predefinedToasts = usePredefinedToasts();

  const { searchParams: filters, navigate } = useSearchParams(
    employeesByBenefitFiltersSchema,
  );
  const { selectedRowIds, setSelectedRowIds } = Table.useSelectedRows();

  const { data: assignments, isLoading } = useEmployeesByBenefitAssignments(
    benefitId,
    filters,
  );

  const { mutate: assignEmployeesToBenefit, isLoading: isAssigning } =
    useAssignEmployeesToBenefit();
  const { mutate: exportToXls, isLoading: isExportingToXls } =
    useExportEmployeesByBenefit();

  const selectedCategory = useMemo(() => {
    return (
      assignments?.customCategories.find(
        (c) => c.id === filters.customCategory,
      ) ?? null
    );
  }, [assignments, filters.customCategory]);

  return (
    <>
      <TableWrapper>
        <TableToolbar sticky>
          <Stack gap={1}>
            <CompanyScopeInfoMessage />

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="flex-end"
              gap={1}
            >
              <ActionsMenu
                type="spread"
                items={[
                  {
                    label: t('employees.choose_custom_column'),
                    color: 'primary',
                    disabled: !hasViewPermission(['employees']),
                    onClick: async () => {
                      const response = await openModal({
                        component: CustomCategorySelectModal,
                        props: {
                          options: assignments?.customCategories ?? [],
                          selected: filters.customCategory,
                        },
                      });

                      if (response.action === 'CONFIRM') {
                        navigate({
                          params: {
                            customCategory: response.category,
                            customCategoryValue: [],
                          },
                        });
                      }
                    },
                  },
                ]}
              />
            </Stack>

            <Divider />

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              gap={1}
            >
              <ListTotal total={assignments?.items.length} />

              <Stack direction="row" alignItems="center" gap={1}>
                <Filters
                  value={filters}
                  initialValues={employeesByBenefitFiltersSchema.parse({})}
                  onChange={(v) => navigate({ params: { ...v, page: 1 } })}
                  onChangeCallback={() => setSelectedRowIds({})}
                >
                  {({ value, initialValues, setFilter }) => (
                    <>
                      {hasViewPermission(['employees']) ? (
                        <FilterButton
                          label={t('entity_type.employee')}
                          value={value.employees}
                          initialValue={initialValues.employees}
                          onChange={(v) => setFilter('employees', v)}
                        >
                          {({ value, applyFilter, clearFilter, close }) => (
                            <EmployeesFilter
                              value={{ value }}
                              onApplyFilter={({ value }) => applyFilter(value)}
                              onClearFilter={clearFilter}
                              onCancel={close}
                            />
                          )}
                        </FilterButton>
                      ) : null}

                      <FilterButton
                        label={t('entity_type.employeeGroup')}
                        value={value.employeeGroups}
                        initialValue={initialValues.employeeGroups}
                        onChange={(v) => setFilter('employeeGroups', v)}
                      >
                        {({ value, applyFilter, clearFilter, close }) => (
                          <EmployeesGroupsFilter
                            value={{ value }}
                            onApplyFilter={({ value }) => applyFilter(value)}
                            onClearFilter={clearFilter}
                            onCancel={close}
                          />
                        )}
                      </FilterButton>

                      <FilterButton
                        label={t('home.organisationStructure_title')}
                        value={value.orgStructure}
                        initialValue={initialValues.orgStructure}
                        onChange={(v) => setFilter('orgStructure', v)}
                        contentMaxWidth={420}
                      >
                        {({ value, applyFilter, clearFilter, close }) => (
                          <OrgStructureFilter
                            value={value}
                            onApplyFilter={applyFilter}
                            onClearFilter={clearFilter}
                            onCancel={close}
                          />
                        )}
                      </FilterButton>

                      {filters.customCategory && selectedCategory ? (
                        <FilterButton
                          label={selectedCategory.name}
                          value={value.customCategoryValue}
                          initialValue={initialValues.customCategoryValue}
                          onChange={(v) => setFilter('customCategoryValue', v)}
                        >
                          {({
                            value: filterValue,
                            applyFilter,
                            clearFilter,
                            close,
                          }) => (
                            <CustomCategoryValuesFilter
                              value={{ value: filterValue }}
                              onApplyFilter={({ value }) => applyFilter(value)}
                              onClearFilter={clearFilter}
                              onCancel={close}
                              customCategoryId={value.customCategory}
                            />
                          )}
                        </FilterButton>
                      ) : null}

                      <FilterButton
                        label={`${t('alternatives.assignmentStatus')} (${t(
                          'alternatives.assignment_type.individual',
                        )})`}
                        value={value.assignedDirectly}
                        initialValue={initialValues.assignedDirectly}
                        onChange={(v) => setFilter('assignedDirectly', v)}
                      >
                        {({ value, applyFilter, clearFilter, close }) => (
                          <SelectFilter
                            value={{ value }}
                            onApplyFilter={({ value }) => applyFilter(value)}
                            onClearFilter={clearFilter}
                            onCancel={close}
                            options={assignmentStatuses.map((status) => ({
                              key: status,
                              label: t(`status.${status}`),
                            }))}
                            searchable={false}
                            multiple={true}
                          />
                        )}
                      </FilterButton>

                      <FilterButton
                        label={`${t('alternatives.assignmentStatus')} (${t(
                          'alternatives.assignment_type.group',
                        )})`}
                        value={value.assignedByGroup}
                        initialValue={initialValues.assignedByGroup}
                        onChange={(v) => setFilter('assignedByGroup', v)}
                      >
                        {({ value, applyFilter, clearFilter, close }) => (
                          <SelectFilter
                            value={{ value }}
                            onApplyFilter={({ value }) => applyFilter(value)}
                            onClearFilter={clearFilter}
                            onCancel={close}
                            options={assignmentStatuses.map((status) => ({
                              key: status,
                              label: t(`status.${status}`),
                            }))}
                            searchable={false}
                            multiple={true}
                          />
                        )}
                      </FilterButton>
                    </>
                  )}
                </Filters>

                <Divider orientation="vertical" variant="middle" flexItem />

                <DownloadXlsAction
                  listLength={assignments?.total ?? 0}
                  exportToExcel={() => exportToXls({ benefitId, filters })}
                  loading={isExportingToXls}
                  disabled={!hasViewPermission(['employees'])}
                />
              </Stack>
            </Stack>
          </Stack>
        </TableToolbar>

        <Table
          data={assignments?.items ?? []}
          isLoading={isLoading}
          columns={[
            {
              id: 'name',
              header: t('common.full_name'),
              cell: ({ row: { original: assignment } }) => {
                return (
                  <EmployeeCell
                    employee={{
                      id: assignment.id,
                      fullName: assignment.fullName,
                    }}
                  />
                );
              },
              meta: {
                sort: true,
              },
            },
            {
              id: 'customCategory',
              header: selectedCategory?.name,
              cell: ({ row: { original: assignment } }) => {
                return (
                  assignment.customCategories.find(
                    (c) => c.id === selectedCategory?.id,
                  )?.value.name ?? ''
                );
              },
            },
            {
              id: 'assignmentStatus',
              header: `${t('alternatives.assignmentStatus')} (${t(
                'alternatives.assignment_type.individual',
              )})`,
              meta: {
                align: 'center',
              },
              cell: ({ row: { original: assignment } }) => {
                return (
                  <Stack
                    flexDirection="row"
                    gap={1.5}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Box width={0.5} textAlign="right">
                      <Typography
                        color={COLOR_BY_STATUS[assignment.assignedDirectly]}
                      >
                        {t(`status.${assignment.assignedDirectly}`)}
                      </Typography>
                    </Box>

                    <Stack width={0.5} alignItems="left" flexDirection="row">
                      {assignment.assignmentCount ? (
                        <Button
                          variant="neutral-outline"
                          size="sm"
                          icon={AccessTime}
                          onClick={() => {
                            openModal({
                              component: EmployeeBenefitAssignmentHistory,
                              props: {
                                benefitId,
                                employeeId: assignment.id,
                                readonly:
                                  !canEditBenefits ||
                                  !hasEditPermission(['benefits']),
                              },
                              size: 'lg',
                            });
                          }}
                          tooltip={t('permissions.historyModule')}
                        />
                      ) : null}
                    </Stack>
                  </Stack>
                );
              },
            },
            {
              id: 'assignmentStatus',
              header: `${t('alternatives.assignmentStatus')} (${t(
                'alternatives.assignment_type.group',
              )})`,
              meta: {
                align: 'center',
              },
              cell: ({ row: { original: assignment } }) => {
                return (
                  <Stack
                    flexDirection="row"
                    gap={1.5}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Box width={0.5} textAlign="right">
                      <Typography
                        color={COLOR_BY_STATUS[assignment.assignedByGroup]}
                      >
                        {t(`status.${assignment.assignedByGroup}`)}
                      </Typography>
                    </Box>

                    <Stack width={0.5} alignItems="left" flexDirection="row">
                      {assignment.assignmentCount ||
                      assignment.assignedByGroup === 'on' ? (
                        <Button
                          variant="neutral-outline"
                          size="sm"
                          icon={ViewTimelineOutlined}
                          onClick={() => {
                            openModal({
                              component: EmployeeBenefitAssignmentTimeline,
                              props: {
                                benefitId,
                                employeeId: assignment.id,
                              },
                              size: 'xl',
                            });
                          }}
                          tooltip={t('permissions.historyModule')}
                        />
                      ) : null}
                    </Stack>
                  </Stack>
                );
              },
            },
            {
              id: 'actions',
              cell: ({ row: { original: assignment } }) => {
                return (
                  <ActionsMenu
                    type="compact"
                    items={[
                      {
                        label: t(
                          `status.${
                            assignment.assignmentStatus === 'on' ? 'off' : 'on'
                          }`,
                        ),
                        disabled:
                          isAssigning ||
                          !canEditBenefits ||
                          !hasEditPermission(['benefits']) ||
                          !hasViewPermission(['employees']),
                        onClick: async () => {
                          const response = await openModal({
                            component: AssignmentDateSelectModal,
                          });

                          if (response.action === 'CONFIRM') {
                            assignEmployeesToBenefit(
                              {
                                benefitId,
                                ...(assignment.assignmentStatus === 'off'
                                  ? {
                                      action: 'create',
                                      data: {
                                        employeeIds: [assignment.id],
                                        ...(response.isNow
                                          ? { fromNow: true }
                                          : { from: response.date }),
                                      },
                                    }
                                  : {
                                      action: 'update',
                                      data: {
                                        employeeIds: [assignment.id],
                                        ...(response.isNow
                                          ? { toNow: true }
                                          : { to: response.date }),
                                      },
                                    }),
                              },
                              {
                                onSuccess: () => {
                                  predefinedToasts.success.updated();
                                },
                              },
                            );
                          }
                        },
                      },
                    ]}
                  />
                );
              },
            },
          ]}
          pagination={
            assignments && {
              count: assignments.total,
              page: assignments.page,
              pageSize: assignments.pageSize,
              pageSizeOptions: APP.pagination.defaults.pageSize,
              onChange: (page, pageSize) => {
                navigate({ params: { page, pageSize } });
              },
            }
          }
          rows={{
            inactive: ({ original: assignment }) =>
              assignment.assignmentStatus === 'off',
          }}
          selection={{
            value: selectedRowIds,
            onChange: setSelectedRowIds,
            actions: assignmentStatuses.map((status) => ({
              label: t(`status.${status}`),
              disabled:
                !canEditBenefits ||
                !hasEditPermission(['benefits']) ||
                !hasViewPermission(['employees']),
              onClick: async () => {
                const response = await openModal({
                  component: AssignmentDateSelectModal,
                });

                if (response.action === 'CONFIRM') {
                  assignEmployeesToBenefit(
                    {
                      benefitId,
                      ...(status === 'on'
                        ? {
                            action: 'create',
                            data: {
                              employeeIds: Object.keys(selectedRowIds),
                              ...(response.isNow
                                ? { fromNow: true }
                                : { from: response.date }),
                            },
                          }
                        : {
                            action: 'update',
                            data: {
                              employeeIds: Object.keys(selectedRowIds),
                              ...(response.isNow
                                ? { toNow: true }
                                : { to: response.date }),
                            },
                          }),
                    },
                    {
                      onSuccess: () => {
                        predefinedToasts.success.updated();
                        setSelectedRowIds({});
                      },
                    },
                  );
                }
              },
            })),
            isLoading: isAssigning,
          }}
        />
      </TableWrapper>

      <Footer visible={guided}>
        <Stack direction="row" justifyContent="end" gap={1}>
          <Button
            label={t('common.continue')}
            variant="primary"
            onClick={() => {
              onSaveSuccess();
            }}
          />
        </Stack>
      </Footer>
    </>
  );
};
